import React, { useEffect, useState, useCallback } from "react";
import {
    Card,
    CardContent,
    Grid,
    Box,
    Typography,
    Button,
} from "@mui/material";

import { ShippingDocumentLineViewModel, ListQuery, DocumentNoProps } from "../../Types/ExportTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import { formatNumber } from '../../Utils/NumberUtils';
import { useTranslation } from "react-i18next";
import ImageBox from "../../components/Image/ImageBox";
import { generateVariantLabelFromShipment } from '../../Utils/VariantUtils';
import ResponsivePagination from "../ResponsivePagination";

const ShipmentDocumentLines: React.FC<DocumentNoProps> = ({ documentNo }) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();

    const { showLoading, hideLoading } = useLoading();
    const [lines, setLines] = useState<ShippingDocumentLineViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const { t } = useTranslation();
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 10, filter: "" });
    const [rowMode, setRowMode] = useState(true);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const fetchShipmentLines = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authService.post<{ items: ShippingDocumentLineViewModel[]; count: number }>(
                `/shipmentDocumentApi/GetShipmentLines?documentNo=${documentNo}`,
                query
            );
            setLines(response.items);
            setTotalCount(response.count);
        } catch (error) {
            console.error("Failed to fetch lines:", error);
        } finally {
            setIsLoading(false);
        }
    }, [authService, documentNo, query]);

    useEffect(() => {
        fetchShipmentLines();
    }, [fetchShipmentLines, query]);

    const toggleRowMode = () => {
        const newRowMode = !rowMode;
        setRowMode(newRowMode);
        setQuery({ ...query, page: 0, pageSize: newRowMode ? 20 : 2 });
    };

    return (
        <Box sx={{ padding: "1rem" }}>
            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={toggleRowMode}
            >
                {t("common.toggleListMode")}
            </Button>

            <Box sx={{ padding: "1rem" }}>
                {rowMode ? (
                    <Grid container spacing={1}>
                        {lines.map((line, index) => (
                            <Grid item xs={12} key={line.id}>
                                <Grid
                                    container
                                    spacing={1}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                                        ":hover": {
                                            backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                                        },
                                    }}
                                >
                                    <Grid item xs={12} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                maxWidth: "80px",
                                                maxHeight: "80px",
                                                height: "auto",
                                                borderRadius: "8px",
                                            }}
                                        >
                                            <ImageBox
                                                imageUrl={line.img_Url ?? ""}
                                                altText={line.description || "No Description"}
                                                fallbackText={t("common.noImage")}
                                                width="80px"
                                                height="80px"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} lg={5}>
                                        <Typography variant="body2" sx={{ color: "gray" }}>
                                            {line.itemNo ?? line.accountNo}
                                        </Typography>
                                        <Typography variant="body1">{line.description}</Typography>
                                        <Typography variant="body2">{generateVariantLabelFromShipment(line)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <Typography variant="body2" sx={{ color: "gray" }}>
                                            {t("common.qty")}: {formatNumber(line.quantity)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {t("common.amount")}: {formatNumber(line.amountIncludingVAT)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                        {lines.map((line) => (
                            <Grid item xs={12} lg={6} key={line.id}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Grid container spacing={1}>
                                            {/* Image */}
                                            <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                    <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                        <ImageBox
                                                            imageUrl={line.img_Url ?? ""}
                                                            altText={line.description || "No Description"}
                                                            fallbackText={t("common.noImage")}
                                                            width="120px"
                                                            height="120px"
                                                        />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            {/* Item No - Bold & Larger */}
                                            <Grid item xs={12}>
                                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>{line.itemNo ?? line.accountNo}</Typography>
                                            </Grid>
                                            {/* Description */}
                                            <Grid item xs={12}>
                                                <Typography sx={{ color: "gray" }}>{line.description}</Typography>
                                            </Grid>
                                            {/* Barcode & Quantity */}
                                            {line.barcode && (
                                                <Grid item xs={6}>
                                                    <Typography variant="body2" sx={{ color: "gray" }}> {t("common.barcode")}:</Typography>
                                                    <Typography variant="body1">{line.barcode}</Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={line.barcode ? 6 : 12}>
                                                <Typography variant="body2" sx={{ fontWeight: "bold" }}> {t("common.qty")}:</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: "bold" }}>{formatNumber(line.quantity)}</Typography>
                                            </Grid>
                                            {/* Amount - Highlighted */}
                                            <Grid item xs={12}>
                                                <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                        {t("common.amount")}: {formatNumber(line.amountIncludingVAT)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ marginTop: "1rem" }}
            />
        </Box>
    );
};

export default ShipmentDocumentLines;